/** Screen-only magnification for the staged label preview (see
 * LabelPreviewStage.tsx). The label's own layout stays at the reference
 * width (512px at 4x6) that the print CSS and the PDF-mirrored metrics are
 * built around -- `zoom` multiplies every metric uniformly, so proportions
 * are preserved by construction while the artifact reads larger against the
 * rest of the page. Print output is unaffected: containerLabelPrint.css
 * resets zoom on the print root.
 *
 * The zoom itself is adaptive (see `previewZoomForStage`), clamped between
 * these two bounds: `PREVIEW_MAX_ZOOM` is the ceiling used whenever the stage
 * has room to spare, `PREVIEW_MIN_ZOOM` is the floor so the artifact never
 * shrinks past legibility on a cramped layout. */
export const PREVIEW_MAX_ZOOM = 1.5;
export const PREVIEW_MIN_ZOOM = 0.75;

/** Fixed on-screen content width per label size for the fixed-width
 * (`fixedWidth`) preview. Mirrors the pdf-generator lambda's own reference
 * canvas width (`LABEL_REFERENCE_WIDTH` = 512 for the 4x6 label, from the
 * shared encamp-shared/src/waste/hazardousWasteLabelVisuals module); LETTER
 * is scaled up from that same reference so the two previews read at
 * consistent relative sizes. Lives here (not LabelPreview.tsx) so
 * LabelPreviewStage's zoom math can import it without a component-file
 * export that trips react-refresh's only-export-components rule. */
export const LABEL_WIDTH_BY_SIZE: Record<"sm" | "lg", number> = { sm: 512, lg: 640 };

// The stage Box's own horizontal padding at the `md` breakpoint (32px per
// side -- see LabelPreviewStage's `p` prop); subtracted from the stage's
// measured width so the fitted zoom leaves room for that padding instead of
// letting the zoomed label butt up against (or overflow past) it.
const PADDING_ALLOWANCE = 64;

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The zoom level that fits `labelWidth` (the label's unzoomed pixel width at
 * its current `size`) inside `stageWidth` (the stage's own measured content
 * width), clamped to [`minZoom`, `maxZoom`].
 *
 * The bounds default to `PREVIEW_MIN_ZOOM`/`PREVIEW_MAX_ZOOM`; a caller
 * embedding the stage in a summary surface (e.g. the container detail page's
 * label section) passes a lower ceiling so the artifact stays
 * thumbnail-sized there, and a floor below `PREVIEW_MIN_ZOOM` so the fit can
 * actually track a stage narrower than the legibility floor assumes. The
 * ceiling is applied after the floor, so a `maxZoom` below `minZoom` wins.
 *
 * `stageWidth <= 0` returns `maxZoom` unconditionally -- a stage that hasn't
 * been measured yet (e.g. a jsdom mount with no ResizeObserver polyfill)
 * keeps the fixed-zoom behavior instead of collapsing to the floor.
 */
export function previewZoomForStage(
  stageWidth: number,
  labelWidth: number,
  maxZoom: number = PREVIEW_MAX_ZOOM,
  minZoom: number = PREVIEW_MIN_ZOOM
): number {
  if (stageWidth <= 0) {
    return maxZoom;
  }
  const fitZoom = (stageWidth - PADDING_ALLOWANCE) / labelWidth;
  return roundToTwoDecimals(Math.min(maxZoom, Math.max(minZoom, fitZoom)));
}
